// Per-user color for avatars/markers. When the user has chosen a custom color
// it wins; otherwise we fall back to a deterministic hue derived from their id,
// so a user without a preference still reads as "their" color consistently
// across the map, the thread, mention chips, etc.
//
// Strategy: FNV-1a hash on the id string → hue 0–359 → saturated mid-
// lightness HSL. Foreground is chosen for legibility on the resulting
// background. For a custom hex, foreground is picked from the color's
// relative luminance so initials stay legible on any pick.
const FALLBACK = { fill: 'hsl(220 5% 45%)', fg: '#ffffff' };
const HEX6 = /^#[0-9a-fA-F]{6}$/;
// Foreground (text) color for an arbitrary #RRGGBB background, via WCAG
// relative luminance: dark text on light backgrounds, white on dark.
const fgForHex = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const lin = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
    const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
    return luminance > 0.5 ? '#181818' : '#ffffff';
};
const hashStringToHue = (s) => {
    let h = 2166136261 >>> 0; // FNV-1a offset basis
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 16777619) >>> 0;
    }
    return h % 360;
};
// Pick a foreground that reads on the generated background. The HSL we
// emit sits in the mid-lightness band so we can flip to white or near-
// black based on a coarse luminance check on the hue band.
const fgForHue = (hue) => {
    // Yellow/green band (60–180) is light enough that black reads better;
    // everything else gets white text.
    if (hue >= 60 && hue <= 180)
        return '#181818';
    return '#ffffff';
};
export const userColor = (userId, customColor) => {
    // A valid chosen color always wins over the deterministic hash.
    if (customColor && HEX6.test(customColor)) {
        return { fill: customColor, fg: fgForHex(customColor) };
    }
    if (!userId)
        return FALLBACK;
    const hue = hashStringToHue(userId);
    return {
        fill: `hsl(${hue} 70% 50%)`,
        fg: fgForHue(hue),
    };
};
